import React, { useState } from 'react';
import { Button, Icon, Tooltip } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils/localization';

interface CopyButtonProps {
  text: string;
}

/** Copy-to-clipboard button with tooltip feedback ("Copy to Clipboard" → "Copied") */
export function CopyButton({ text }: CopyButtonProps): JSX.Element {
  const l = useInvertedLocalize();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        fallbackCopy();
      });
  };

  /** Fallback for environments where navigator.clipboard.writeText fails (e.g. non-HTTPS) */
  const fallbackCopy = () => {
    try {
      const textarea = document.createElement('textarea');

      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write failed — no user-facing action needed
    }
  };

  return (
    <Tooltip
      text={copied ? l('Copied') : l('Copy to Clipboard')}
      key={copied ? 'copied' : 'copy'}>
      <Button
        kind='ghost'
        size='small'
        leftIcon={<Icon kind='Copy' size='small' />}
        ariaLabel={l('Copy to Clipboard')}
        onClick={handleCopy}
      />
    </Tooltip>
  );
}
